import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles, Check } from "lucide-react";
import { api } from "@/lib/api";
import { trackLead } from "@/lib/analytics";

const DISMISS_KEY = "kc_festive_popup_until";
const DONE_KEY = "kc_festive_subscribed";
const SHOW_AFTER_MS = 7000;
const RESHOW_DAYS = 7;

/** Festive lead-capture popup: first-dibs hook for festive drops and new arrivals. */
export const FestiveSignupPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.startsWith("/admin")) return undefined;
    if (localStorage.getItem(DONE_KEY)) return undefined;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until > Date.now()) return undefined;
    const t = setTimeout(() => setOpen(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && dismiss();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + RESHOW_DAYS * 86400000));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/leads", { email: email.trim(), source: "festive_popup" });
      trackLead("festive_popup");
      localStorage.setItem(DONE_KEY, "1");
      setDone(true);
      setTimeout(() => setOpen(false), 2600);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — try again.");
    } finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={dismiss} data-testid="festive-popup-backdrop">
      <div className="relative bg-[#1A1A1A] text-[#FFFFFF] max-w-md w-full p-8 sm:p-10 shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="festive-popup">
        <button onClick={dismiss} aria-label="Close popup" data-testid="festive-popup-close"
          className="absolute top-3 right-3 p-2 text-[#FFFFFF]/60 hover:text-[#FFFFFF] transition-colors">
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="festive-popup-success">
            <span className="inline-flex w-12 h-12 rounded-full border border-[#25D366] items-center justify-center">
              <Check size={22} className="text-[#25D366]" />
            </span>
            <h3 className="font-display text-3xl tracking-tight mt-4">You're on the list</h3>
            <p className="text-sm text-[#FFFFFF]/75 font-body mt-2">Watch your inbox — the next festive drop lands there first.</p>
          </div>
        ) : (
          <>
            <div className="overline text-[#B84A3B] flex items-center gap-2 mb-3">
              <Sparkles size={13} /> Festive Early Access
            </div>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05]">
              First dibs on festive drops &amp; new arrivals
            </h3>
            <p className="text-sm text-[#FFFFFF]/75 font-body mt-3 leading-relaxed">
              New styles drop daily and sell out fast. Join the list and shop new arrivals and sale edits before anyone else.
            </p>
            <form onSubmit={submit} className="mt-6" data-testid="festive-popup-form">
              <div className="flex border border-[#FFFFFF]/25 focus-within:border-[#FFFFFF] transition-colors">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address" data-testid="festive-popup-email"
                  className="flex-1 bg-transparent px-4 py-3.5 text-sm font-body placeholder:text-[#FFFFFF]/40 focus:outline-none" />
                <button type="submit" disabled={sending} data-testid="festive-popup-submit"
                  className="bg-[#B84A3B] px-5 text-[0.66rem] font-mono tracking-[0.18em] uppercase hover:bg-[#FFFFFF] hover:text-[#1A1A1A] transition-colors disabled:opacity-60">
                  {sending ? "…" : "Notify Me"}
                </button>
              </div>
              {error && <p className="text-xs text-[#E57373] font-body mt-2" data-testid="festive-popup-error">{error}</p>}
              <p className="text-[0.65rem] text-[#FFFFFF]/45 font-body mt-3">No spam, ever. Unsubscribe anytime.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
